import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  Button,
  Input,
  Select,
  Space,
  message,
  Card,
  Row,
  Col,
  Form,
  Typography,
  Statistic,
  DatePicker,
  TimePicker,
  Modal,
  Descriptions,
  Tag,
  List,
  Image,
  Tooltip,
  Empty
} from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  FilterOutlined,
  UserOutlined,
  FileTextOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  CarOutlined,
  FileImageOutlined,
  FilePdfOutlined,
  EyeOutlined,
  DownloadOutlined,
  PaperClipOutlined,
  DollarOutlined
} from '@ant-design/icons';
import { all_routes } from '../../../router/all_routes';
import fieldActivityService from '../../../services/field-activity/field-activity.service';
import userService from '../../../services/user/user.service';
import vehicleService from '../../../services/vehicle/vehicle.service';
import leaveService from '../../../services/leave/leave.service';
import { Vehicle } from '../../../types/vehicle.types';
import {
  FieldActivity,
  CreateFieldActivityDTO,
  UpdateFieldActivityDTO,
  ActivityType,
  ActivityStatus,
  LocationLog,
  ActivityWithLocations,
  APPROVAL_LOCKED_ACTIVITY_STATUSES
} from '../../../types/field.types';
import ApprovalFlowTags from '../../shared/approval/ApprovalFlowTags';
import { User, EmploymentType } from '../../../types/user.types';
import { handleApiError } from '../../../utils/errorHandler';
import LoadingSpinner from '../../shared/uiInterface/base-ui/LoadingSpinner';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import FileService from '../../../services/file/file.service';
import fieldModeRequestService from '../../../services/fieldModeRequest/fieldModeRequest.service';
import { FieldModeSession } from '../../../types/fieldModeRequest.types';
import FieldModeSessionsTable from '../fieldModeRequests/components/FieldModeSessionsTable';

// Components
import EmployeeHeader from './components/EmployeeHeader';
import RouteMap from './components/RouteMap';
import ActivityTable from './components/ActivityTable';
import ActivityFormModal from './components/ActivityFormModal';
import ActivityLocationHistoryModal from './components/ActivityLocationHistoryModal';

// Leaflet ikon düzeltmesi
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const { Option } = Select;
const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { TextArea } = Input;

const getActivityTypeLabelTR = (type: ActivityType) => {
  const labels: Record<ActivityType, string> = {
    [ActivityType.VISIT]: 'Ziyaret',
    [ActivityType.DELIVERY]: 'Teslimat',
    [ActivityType.MAINTENANCE]: 'Bakım',
    [ActivityType.OFFICE]: 'Ofis',
    [ActivityType.OTHER]: 'Diğer',
  };
  return labels[type] ?? type;
};

const getActivityStatusLabelTR = (status: ActivityStatus) => {
  const labels: Record<ActivityStatus, string> = {
    [ActivityStatus.PENDING_APPROVAL]: 'Onay Bekliyor',
    [ActivityStatus.PLANNED]: 'Planlandı',
    [ActivityStatus.COMPLETED]: 'Tamamlandı',
    [ActivityStatus.CANCELLED]: 'İptal Edildi',
    [ActivityStatus.REJECTED]: 'Reddedildi',
  };
  return labels[status] ?? status;
};

const getActivityStatusColor = (status: ActivityStatus) => {
  const colors: Record<ActivityStatus, string> = {
    [ActivityStatus.PENDING_APPROVAL]: 'gold',
    [ActivityStatus.PLANNED]: 'blue',
    [ActivityStatus.COMPLETED]: 'green',
    [ActivityStatus.CANCELLED]: 'default',
    [ActivityStatus.REJECTED]: 'red',
  };
  return colors[status] ?? 'default';
};

const PersonelActivityDetailPage: React.FC = () => {
  const { t } = useTranslation();
  const routes = all_routes;
  const navigate = useNavigate();
  const { userId } = useParams<{ userId: string }>();
  const location = useLocation();

  // Forms
  const [createForm] = Form.useForm();
  const [editForm] = Form.useForm();

  // Data States
  const [activities, setActivities] = useState<FieldActivity[]>([]);
  const [filteredActivities, setFilteredActivities] = useState<FieldActivity[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState<FieldActivity | null>(null);

  // Modal States
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [detailsModalVisible, setDetailsModalVisible] = useState(false);
  const [annualLeaveBalance, setAnnualLeaveBalance] = useState<{
    limit?: number;
    remaining?: number;
    unit?: string;
  } | null>(null);

  // Filter States
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<ActivityStatus | undefined>(undefined);
  const [dateRange, setDateRange] = useState<[string, string] | null>(null);
  const [timeRange, setTimeRange] = useState<[string, string] | null>(null);
  const [isSameDay, setIsSameDay] = useState(false);
  
  // Employee Selection & Route Tracking
  const [selectedEmployee, setSelectedEmployee] = useState<User | null>(null);
  const [selectedDate, setSelectedDate] = useState<string>(dayjs().format('YYYY-MM-DD'));
  const [locationRoute, setLocationRoute] = useState<LocationLog[]>([]);
  const [activitiesWithLocations, setActivitiesWithLocations] = useState<ActivityWithLocations[]>([]);
  const [routeLoading, setRouteLoading] = useState(false);
  const [showMap, setShowMap] = useState(false);
  
  // Aktivite konum geçmişi modal
  const [locationHistoryModalVisible, setLocationHistoryModalVisible] = useState(false);
  const [selectedActivityForLocation, setSelectedActivityForLocation] = useState<FieldActivity | null>(null);

  // Hibrit çalışan: günlük rotada boş gün bilgisi ve saha kayıtları
  const [routeEmptyForDate, setRouteEmptyForDate] = useState<string | null>(null);
  const [fieldSessions, setFieldSessions] = useState<FieldModeSession[]>([]);
  const [fieldSessionsLoading, setFieldSessionsLoading] = useState(false);

  // Computed Values
  // Saha ve hibrit çalışanlar sahaya çıkabilir: rota, araç ve masraf alanları ikisi için de gösterilir
  const isHybrid = selectedEmployee?.employeeType === EmploymentType.HYBRID;
  const canWorkInField = selectedEmployee?.employeeType === EmploymentType.FIELD || isHybrid;

  // Saha kayıtları tarih aralığı: sayfadaki tarih filtresi, yoksa son 7 gün
  const [fieldSessionsStart, fieldSessionsEnd] = useMemo<[string, string]>(() => {
    if (dateRange && dateRange[0] && dateRange[1]) {
      return [dayjs(dateRange[0]).format('YYYY-MM-DD'), dayjs(dateRange[1]).format('YYYY-MM-DD')];
    }
    return [dayjs().subtract(6, 'day').format('YYYY-MM-DD'), dayjs().format('YYYY-MM-DD')];
  }, [dateRange]);

  const stats = {
    total: activities.length,
    planned: activities.filter(a => a.status === ActivityStatus.PLANNED).length,
    completed: activities.filter(a => a.status === ActivityStatus.COMPLETED).length,
    totalExpenses: activities.reduce((sum, a) => sum + (a.totalExpenses || 0), 0)
  };

  // Load user from URL or state
  useEffect(() => {
    if (userId) {
      const userFromState = location.state?.user as User | undefined;
      
      if (userFromState && userFromState.id === parseInt(userId)) {
        setSelectedEmployee(userFromState);
        fetchUserById(parseInt(userId));
        
        // Set default date range to today
        const startOfDay = dayjs().startOf('day').toISOString();
        const endOfDay = dayjs().endOf('day').toISOString();
        setDateRange([startOfDay, endOfDay]);
        setIsSameDay(true);
        setTimeRange(null);
      } else {
        // If user not in state, fetch from API
        fetchUserById(parseInt(userId));
      }
    } else {
      // No userId, redirect to selection page
      navigate(routes.personelActivities, { replace: true });
    }
  }, [userId, location.state]);

  // Fetch activities when employee or filters change
  useEffect(() => {
    if (selectedEmployee) {
      fetchActivities();
    }
  }, [selectedEmployee, dateRange, statusFilter, timeRange]);

  // Apply local filters
  useEffect(() => {
    applyFilters();
  }, [activities, searchTerm]);

  // Initial data fetch
  useEffect(() => {
    fetchUsers();
    fetchVehicles();
  }, []);

  // Hibrit çalışanın saha kayıtları (tarih filtresi değiştikçe)
  useEffect(() => {
    if (selectedEmployee && isHybrid) {
      fetchFieldSessions();
    } else {
      setFieldSessions([]);
    }
  }, [selectedEmployee?.id, isHybrid, fieldSessionsStart, fieldSessionsEnd]);

  const fetchUserById = async (id: number) => {
    try {
      const user = await userService.getUserById(id);
      
      if (user && user.status === 'active' && user.role !== 'admin') {
        setSelectedEmployee(user);
        
        // Set default date range to today
        const startOfDay = dayjs().startOf('day').toISOString();
        const endOfDay = dayjs().endOf('day').toISOString();
        setDateRange([startOfDay, endOfDay]);
        setIsSameDay(true);
        setTimeRange(null);
      } else {
        message.error('Personel bulunamadı!');
        navigate(routes.personelActivities, { replace: true });
      }
    } catch (error: any) {
      handleApiError(error, t);
      navigate(routes.personelActivities, { replace: true });
    }
  };

  const fetchUsers = async () => {
    try {
      const userData = await userService.getUsers();
      const activeUsers = userData.filter((user) => 
        user.status === 'active' && user.role !== 'admin'
      );
      setUsers(activeUsers);
    } catch (error: any) {
      handleApiError(error, t);
    }
  };

  const fetchVehicles = async () => {
    try {
      const vehicleData = await vehicleService.getVehicles();
      setVehicles(vehicleData);
    } catch (error: any) {
      handleApiError(error, t);
    }
  };

  const fetchActivities = async () => {
    if (!selectedEmployee) return;

    setLoading(true);
    try {
      let startISO: string | undefined;
      let endISO: string | undefined;

      if (dateRange && dateRange[0] && dateRange[1]) {
        const start = dayjs(dateRange[0]);
        const end = dayjs(dateRange[1]);

        if (isSameDay && timeRange && timeRange[0] && timeRange[1]) {
          // Aynı gün ve saat aralığı var
          const [startTime, endTime] = timeRange;
          const [startHour, startMinute] = startTime.split(':');
          const [endHour, endMinute] = endTime.split(':');

          startISO = start.hour(parseInt(startHour)).minute(parseInt(startMinute)).second(0).toISOString();
          endISO = end.hour(parseInt(endHour)).minute(parseInt(endMinute)).second(59).toISOString();
        } else {
          // Sadece tarih aralığı
          startISO = start.startOf('day').toISOString();
          endISO = end.endOf('day').toISOString();
        }
      }

      const activitiesData = await fieldActivityService.getFieldActivities({
        assignedToIds: selectedEmployee.id.toString(),
        status: statusFilter,
        startDate: startISO,
        endDate: endISO
      });

      // Normalize assignedTo to assignedUsers
      const normalizedActivities = activitiesData.map(activity => {
        if (activity.assignedTo && !activity.assignedUsers) {
          const assignedUsers = Array.isArray(activity.assignedTo) 
            ? activity.assignedTo 
            : [activity.assignedTo];
          return { ...activity, assignedUsers };
        }
        return activity;
      });

      setActivities(normalizedActivities);
    } catch (error: any) {
      handleApiError(error, t);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...activities];

    if (searchTerm) {
      filtered = filtered.filter(
        (activity) =>
          activity.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          activity.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredActivities(filtered);
  };

  const resetFilters = () => {
    setSearchTerm('');
    setStatusFilter(undefined);
    setDateRange(null);
    setTimeRange(null);
    setIsSameDay(false);
  };

  const fetchLocationRoute = async () => {
    if (!selectedEmployee) return;
    
    setRouteLoading(true);
    try {
      const response = await fieldActivityService.getUserLocationRoute(
        selectedEmployee.id,
        selectedDate
      );
      console.log(response);
      if (!response.route || response.route.length === 0) {
        if (isHybrid) {
          // Hibrit çalışan konum sadece saha modundayken kaydedilir; kart içinde açıklama gösterilir
          setRouteEmptyForDate(selectedDate);
        } else {
          message.warning('Seçili tarihte lokasyon verisi bulunamadı');
        }
        setShowMap(false);
        setLocationRoute([]);
        setActivitiesWithLocations([]);
      } else {
        setRouteEmptyForDate(null);
        setLocationRoute(response.route);
        setActivitiesWithLocations(response.activities || []);
        setShowMap(true);
      }
    } catch (error: any) {
      handleApiError(error, t);
      setShowMap(false);
      setLocationRoute([]);
      setActivitiesWithLocations([]);
    } finally {
      setRouteLoading(false);
    }
  };

  const fetchFieldSessions = async () => {
    if (!selectedEmployee) return;

    setFieldSessionsLoading(true);
    try {
      const sessions = await fieldModeRequestService.getFieldModeSessions({
        userId: selectedEmployee.id,
        startDate: fieldSessionsStart,
        endDate: fieldSessionsEnd,
      });
      setFieldSessions(Array.isArray(sessions) ? sessions : []);
    } catch (error: any) {
      handleApiError(error, t);
    } finally {
      setFieldSessionsLoading(false);
    }
  };

  const handleCreateActivity = async (values: any) => {
    if (!selectedEmployee) return;

    try {
      console.log(values);
      const activityData: CreateFieldActivityDTO = {
        title: values.title,
        description: values.description,
        type: values.type,
        startDate: values.dates[0].toISOString(),
        endDate: values.dates[1].toISOString(),
        userIds: [selectedEmployee.id],
        isBillable: values.isBillable || false,
        vehicleId: values.vehicleId || undefined
      };

      await fieldActivityService.createFieldActivity(activityData);
      console.log(activityData);
      message.success('Aktivite başarıyla oluşturuldu!');
      setCreateModalVisible(false);
      createForm.resetFields();
      fetchActivities();
    } catch (error: any) {
      console.log(error);
      handleApiError(error, t);
    }
  };

  const handleUpdateActivity = async (values: any) => {
    if (!selectedActivity) return;

    try {
      const updateData: UpdateFieldActivityDTO = {
        status: values.status,
        completionNotes: values.completionNotes
      };

      await fieldActivityService.updateFieldActivity(selectedActivity.id, updateData);
      message.success('Aktivite başarıyla güncellendi!');
      setEditModalVisible(false);
      editForm.resetFields();
      fetchActivities();
    } catch (error: any) {
      handleApiError(error, t);
    }
  };

  const handleDeleteActivity = async (activityId: number) => {
    try {
      await fieldActivityService.deleteFieldActivity(activityId);
      message.success('Aktivite başarıyla silindi!');
      fetchActivities();
    } catch (error: any) {
      handleApiError(error, t);
    }
  };

  const handleViewDetails = async (activityId: number) => {
    try {
      const [activity, refreshedEmployee, comprehensiveLeaveBalance] = await Promise.all([
        fieldActivityService.getFieldActivityById(activityId),
        selectedEmployee ? userService.getUserById(selectedEmployee.id) : Promise.resolve(null),
        selectedEmployee ? leaveService.getComprehensiveLeaveBalance(selectedEmployee.id) : Promise.resolve(null),
      ]);
      
      // Normalize assignedTo to assignedUsers
      if (activity.assignedTo && !activity.assignedUsers) {
        const assignedUsers = Array.isArray(activity.assignedTo) 
          ? activity.assignedTo 
          : [activity.assignedTo];
        activity.assignedUsers = assignedUsers;
      }

      if (refreshedEmployee) {
        setSelectedEmployee(refreshedEmployee);
      }

      const annualBalance =
        comprehensiveLeaveBalance?.leaveBalance?.annual ||
        comprehensiveLeaveBalance?.data?.leaveBalance?.annual ||
        null;
      setAnnualLeaveBalance(annualBalance);
      
      setSelectedActivity(activity);
      setDetailsModalVisible(true);
    } catch (error: any) {
      handleApiError(error, t);
    }
  };

  const handleShowLocationHistory = (activity: FieldActivity) => {
    setSelectedActivityForLocation(activity);
    setLocationHistoryModalVisible(true);
  };

  const handleBackToUserSelection = () => {
    navigate(routes.personelActivities);
  };

  if (!selectedEmployee) {
    return <LoadingSpinner centered />;
  }

  return (
    <>
      <div className="page-wrapper vh-100 d-flex flex-column justify-content-between">
        <div className="content flex-fill h-100">
          {/* Page Header */}
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
            <div className="my-auto mb-2">
              <div>
                <h2 className="mb-1">
                  {selectedEmployee.firstName} {selectedEmployee.lastName} - Aktivite Takibi
                </h2>
                <nav>
                  <ol className="breadcrumb mb-0">
                    <li className="breadcrumb-item">
                      <Link to={routes.adminDashboard}>
                        <i className="ti ti-smart-home" />
                      </Link>
                    </li>
                    <li className="breadcrumb-item">Yönetim</li>
                    <li className="breadcrumb-item">
                      <Link to={routes.personelActivities}>Personel Aktiviteleri</Link>
                    </li>
                    <li className="breadcrumb-item active">
                      {selectedEmployee.firstName} {selectedEmployee.lastName}
                    </li>
                  </ol>
                </nav>
              </div>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap" style={{ gap: '8px' }}>
              <Button
                icon={<UserOutlined />}
                onClick={handleBackToUserSelection}
                size="large"
              >
                Personel Değiştir
              </Button>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setCreateModalVisible(true)}
                size="large"
              >
                Yeni Aktivite
              </Button>
            </div>
          </div>

          {/* Employee Header */}
          <EmployeeHeader
            employee={selectedEmployee}
            selectedDate={selectedDate}
            onDateChange={setSelectedDate}
            onShowRoute={fetchLocationRoute}
            onHideMap={() => setShowMap(false)}
            showMap={showMap}
            routeLoading={routeLoading}
          />

          {/* Route Map (field & hybrid workers) */}
          {canWorkInField && showMap && locationRoute.length > 0 && (
            <RouteMap
              locationRoute={locationRoute}
              activities={activitiesWithLocations}
            />
          )}

          {/* Hibrit çalışan: seçili günde saha modunda konum kaydı yok */}
          {isHybrid && !showMap && routeEmptyForDate === selectedDate && (
            <Card style={{ marginBottom: '24px' }}>
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={`Bu gün saha modunda konum kaydı yok (${dayjs(selectedDate).format('DD.MM.YYYY')})`}
              />
            </Card>
          )}

          {/* Statistics Cards */}
          <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Toplam Aktivite"
                  value={stats.total}
                  prefix={<FileTextOutlined />}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Planlanmış"
                  value={stats.planned}
                  prefix={<ClockCircleOutlined />}
                  valueStyle={{ color: '#faad14' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Tamamlanan"
                  value={stats.completed}
                  prefix={<CheckCircleOutlined />}
                  valueStyle={{ color: '#52c41a' }}
                />
              </Card>
            </Col>
            {canWorkInField && (
              <Col xs={24} sm={12} lg={6}>
                <Card>
                  <Statistic
                    title="Toplam Masraf"
                    value={stats.totalExpenses}
                    prefix="₺"
                    precision={2}
                    valueStyle={{ color: '#ff4d4f' }}
                  />
                </Card>
              </Col>
            )}
          </Row>

          {/* Filters */}
          <Card style={{ marginBottom: '16px' }}>
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} lg={6}>
                <Input
                  placeholder="Aktivite ara..."
                  prefix={<SearchOutlined />}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  allowClear
                />
              </Col>
              <Col xs={24} sm={12} lg={5}>
                <RangePicker
                  style={{ width: '100%' }}
                  format="DD.MM.YYYY"
                  placeholder={['Başlangıç', 'Bitiş']}
                  value={dateRange ? [dayjs(dateRange[0]), dayjs(dateRange[1])] : null}
                  onChange={(dates) => {
                    if (dates && dates[0] && dates[1]) {
                      const start = dates[0];
                      const end = dates[1];
                      
                      // Aynı gün mü kontrol et
                      const sameDayCheck = start.format('YYYY-MM-DD') === end.format('YYYY-MM-DD');
                      setIsSameDay(sameDayCheck);
                      
                      // Farklı günler seçilirse saat filtresini temizle
                      if (!sameDayCheck) {
                        setTimeRange(null);
                      }
                      
                      setDateRange([
                        start.toISOString(),
                        end.toISOString()
                      ]);
                    } else {
                      setDateRange(null);
                      setIsSameDay(false);
                      setTimeRange(null);
                    }
                  }}
                  allowClear
                />
              </Col>
              
              {/* Saat Aralığı - Sadece aynı gün seçildiğinde göster */}
              {isSameDay && (
                <Col xs={24} sm={12} lg={5}>
                  <TimePicker.RangePicker
                    style={{ width: '100%' }}
                    format="HH:mm"
                    placeholder={['Başlangıç saati', 'Bitiş saati']}
                    value={timeRange ? [
                      dayjs(timeRange[0], 'HH:mm'),
                      dayjs(timeRange[1], 'HH:mm')
                    ] : null}
                    onChange={(times) => {
                      if (times && times[0] && times[1]) {
                        setTimeRange([
                          times[0].format('HH:mm'),
                          times[1].format('HH:mm')
                        ]);
                      } else {
                        setTimeRange(null);
                      }
                    }}
                    allowClear
                  />
                </Col>
              )}
              <Col xs={24} sm={12} lg={6}>
                <Select
                  placeholder="Durum filtrele"
                  value={statusFilter}
                  onChange={setStatusFilter}
                  style={{ width: '100%' }}
                  allowClear
                >
                  <Option value={ActivityStatus.PENDING_APPROVAL}>Onay Bekliyor</Option>
                  <Option value={ActivityStatus.PLANNED}>Planlandı</Option>
                  <Option value={ActivityStatus.COMPLETED}>Tamamlandı</Option>
                  <Option value={ActivityStatus.CANCELLED}>İptal Edildi</Option>
                  <Option value={ActivityStatus.REJECTED}>Reddedildi</Option>
                </Select>
              </Col>
            </Row>
            <div style={{ marginTop: '16px', textAlign: 'right' }}>
              <Button onClick={resetFilters} icon={<FilterOutlined />}>
                Filtreleri Temizle
              </Button>
            </div>
          </Card>

          {/* Activities Table */}
          <Card>
            <ActivityTable
              activities={filteredActivities}
              loading={loading}
              isFieldWorker={canWorkInField}
              onView={handleViewDetails}
              onEdit={(activity) => {
                setSelectedActivity(activity);
                editForm.setFieldsValue({
                  status: activity.status,
                  completionNotes: activity.completionNotes
                });
                setEditModalVisible(true);
              }}
              onDelete={(id) => handleDeleteActivity(id)}
              onShowLocationHistory={handleShowLocationHistory}
            />
          </Card>

          {/* Saha Kayıtları (hibrit çalışan: sahaya çıkış → ofise dönüş) */}
          {isHybrid && (
            <Card
              style={{ marginTop: '16px' }}
              title={
                <Space>
                  <CarOutlined style={{ color: '#1890ff' }} />
                  <span>Saha Kayıtları</span>
                </Space>
              }
              extra={
                <Space wrap>
                  <Text type="secondary">
                    {dayjs(fieldSessionsStart).format('DD.MM.YYYY')} → {dayjs(fieldSessionsEnd).format('DD.MM.YYYY')}
                  </Text>
                  <Button size="small" onClick={fetchFieldSessions} loading={fieldSessionsLoading}>
                    Yenile
                  </Button>
                </Space>
              }
            >
              <FieldModeSessionsTable sessions={fieldSessions} loading={fieldSessionsLoading} />
            </Card>
          )}
        </div>
      </div>

      {/* Create Activity Modal */}
      <ActivityFormModal
        visible={createModalVisible}
        onCancel={() => {
          setCreateModalVisible(false);
          createForm.resetFields();
        }}
        onFinish={handleCreateActivity}
        form={createForm}
        users={users}
        vehicles={vehicles}
        isFieldWorker={canWorkInField}
        selectedEmployee={selectedEmployee}
        title="Yeni Aktivite Oluştur"
      />

      {/* Edit Activity Modal */}
      <Modal
        title="Aktiviteyi Güncelle"
        open={editModalVisible}
        onCancel={() => {
          setEditModalVisible(false);
          editForm.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form form={editForm} layout="vertical" onFinish={handleUpdateActivity}>
          <Form.Item
            name="status"
            label="Durum"
            rules={[{ required: true, message: 'Lütfen durum seçin!' }]}
            extra={
              selectedActivity && APPROVAL_LOCKED_ACTIVITY_STATUSES.includes(selectedActivity.status)
                ? 'Onay bekleyen veya reddedilen aktivite planlanamaz/tamamlanamaz; sadece iptal edilebilir.'
                : undefined
            }
          >
            {selectedActivity && APPROVAL_LOCKED_ACTIVITY_STATUSES.includes(selectedActivity.status) ? (
              <Select>
                <Option value={selectedActivity.status} disabled>
                  {getActivityStatusLabelTR(selectedActivity.status)}
                </Option>
                <Option value={ActivityStatus.CANCELLED}>İptal Edildi</Option>
              </Select>
            ) : (
              <Select>
                <Option value={ActivityStatus.PLANNED}>Planlandı</Option>
                <Option value={ActivityStatus.COMPLETED}>Tamamlandı</Option>
                <Option value={ActivityStatus.CANCELLED}>İptal Edildi</Option>
              </Select>
            )}
          </Form.Item>

          <Form.Item name="completionNotes" label="Tamamlanma Notları">
            <TextArea rows={4} placeholder="Aktivite tamamlandıktan sonra notlarınızı ekleyin..." />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setEditModalVisible(false)}>İptal</Button>
              <Button type="primary" htmlType="submit">
                Güncelle
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Details Modal */}
      <Modal
        title="Aktivite Detayları"
        open={detailsModalVisible}
        onCancel={() => {
          setDetailsModalVisible(false);
          setSelectedActivity(null);
          setAnnualLeaveBalance(null);
        }}
        footer={null}
        width={800}
      >
        {selectedActivity && (
          <div>
            <Descriptions bordered column={2}>
              <Descriptions.Item label="Başlık" span={2}>
                {selectedActivity.title}
              </Descriptions.Item>
              <Descriptions.Item label="Tip">
                {getActivityTypeLabelTR(selectedActivity.type)}
              </Descriptions.Item>
              <Descriptions.Item label="Durum">
                <Tag color={getActivityStatusColor(selectedActivity.status)}>
                  {getActivityStatusLabelTR(selectedActivity.status)}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Başlangıç Tarihi">
                {dayjs(selectedActivity.startDate).format('DD.MM.YYYY')}
              </Descriptions.Item>
              <Descriptions.Item label="Planlanan Bitiş Tarihi">
                {dayjs(selectedActivity.endDate).format('DD.MM.YYYY')}
              </Descriptions.Item>
              <Descriptions.Item label="Yıllık İzin Limiti">
                {annualLeaveBalance?.limit != null
                  ? `${annualLeaveBalance.limit} ${annualLeaveBalance.unit || 'gün'}`
                  : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Kalan Yıllık İzin">
                {annualLeaveBalance?.remaining != null
                  ? `${annualLeaveBalance.remaining} ${annualLeaveBalance.unit || 'gün'}`
                  : '-'}
              </Descriptions.Item>
              {selectedActivity.status === ActivityStatus.COMPLETED && (
                <Descriptions.Item label="Tamamlandı" span={2}>
                  {selectedActivity.completedAt
                    ? dayjs(selectedActivity.completedAt).format('DD.MM.YYYY HH:mm')
                    : '-'}
                </Descriptions.Item>
              )}
              {canWorkInField && (
                <>
                  <Descriptions.Item label="Faturalandırılabilir">
                    {selectedActivity.isBillable ? (
                      <Tag color="green">Evet</Tag>
                    ) : (
                      <Tag color="red">Hayır</Tag>
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label="Toplam Masraf">
                    ₺{selectedActivity.totalExpenses?.toFixed(2) || '0.00'}
                  </Descriptions.Item>
                </>
              )}
              {canWorkInField && selectedActivity.vehicle && (
                <Descriptions.Item label="Araç" span={2}>
                  <Space>
                    <CarOutlined style={{ color: '#1890ff' }} />
                    <Text strong>{selectedActivity.vehicle.name}</Text>
                    <Tag color="blue">{selectedActivity.vehicle.plateNumber}</Tag>
                    {selectedActivity.vehicle.assignmentType === 'pool' && <Tag color="purple">Havuz</Tag>}
                  </Space>
                </Descriptions.Item>
              )}
              {selectedActivity.status === ActivityStatus.REJECTED && selectedActivity.rejectionReason && (
                <Descriptions.Item label="Red Nedeni" span={2}>
                  <Text type="danger">{selectedActivity.rejectionReason}</Text>
                </Descriptions.Item>
              )}
              {selectedActivity.reviewedAt && (
                <Descriptions.Item
                  label={selectedActivity.status === ActivityStatus.REJECTED ? 'Reddeden' : 'Onaylayan'}
                  span={2}
                >
                  {selectedActivity.approvedBy
                    ? `${selectedActivity.approvedBy.firstName} ${selectedActivity.approvedBy.lastName}`
                    : '-'}
                  {' · '}
                  {dayjs(selectedActivity.reviewedAt).format('DD.MM.YYYY HH:mm')}
                </Descriptions.Item>
              )}
              {selectedActivity.approvalFlow && selectedActivity.approvalFlow.length > 0 && (
                <Descriptions.Item label="Onay Akışı" span={2}>
                  <ApprovalFlowTags steps={selectedActivity.approvalFlow} />
                </Descriptions.Item>
              )}
              <Descriptions.Item label="Açıklama" span={2}>
                {selectedActivity.description || '-'}
              </Descriptions.Item>
              {selectedActivity.completionNotes && (
                <Descriptions.Item label="Tamamlanma Notları" span={2}>
                  {selectedActivity.completionNotes}
                </Descriptions.Item>
              )}
            </Descriptions>

            {/* Masraflar Section */}
            {selectedActivity.expenses && selectedActivity.expenses.length > 0 && (
              <Card
                title={
                  <Space>
                    <DollarOutlined style={{ color: '#52c41a' }} />
                    <Text strong>Masraflar ({selectedActivity.expenses.length})</Text>
                  </Space>
                }
                style={{ marginTop: '16px' }}
                size="small"
              >
                <List
                  dataSource={selectedActivity.expenses}
                  renderItem={(e: any) => {
                    const amount = typeof e.amount === 'string' ? Number(e.amount) : (e.amount ?? 0);
                    const receiptUrl = e.receiptUrl || e.receiptPath;
                    return (
                      <List.Item
                        key={e.id}
                        actions={
                          receiptUrl
                            ? [
                                <Button
                                  type="link"
                                  size="small"
                                  icon={<EyeOutlined />}
                                  key="receipt"
                                  onClick={async () => {
                                    try {
                                      const token = localStorage.getItem('token');
                                      const baseURL = (process.env.REACT_APP_API_URL || '').replace(/\/+$/, '');
                                      const url = `${baseURL}/field-activities/receipts/${encodeURIComponent(receiptUrl)}`;
                                      const res = await fetch(url, {
                                        method: 'GET',
                                        headers: { Authorization: token ? `Bearer ${token}` : '' },
                                      });
                                      if (!res.ok) throw new Error('Fiş yüklenemedi');
                                      const blob = await res.blob();
                                      const blobUrl = window.URL.createObjectURL(blob);
                                      window.open(blobUrl, '_blank');
                                      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
                                    } catch {
                                      message.error('Fiş açılamadı');
                                    }
                                  }}
                                >
                                  Fiş
                                </Button>,
                              ]
                            : undefined
                        }
                      >
                        <List.Item.Meta
                          title={
                            <Space>
                              <Text>{e.description || '-'}</Text>
                              <Tag>{e.currency || 'TRY'}</Tag>
                            </Space>
                          }
                          description={
                            <Space>
                              <Text type="secondary">
                                {e.expenseDate ? dayjs(e.expenseDate).format('DD.MM.YYYY') : '-'}
                              </Text>
                              <Text strong>{amount.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺</Text>
                            </Space>
                          }
                        />
                      </List.Item>
                    );
                  }}
                />
              </Card>
            )}

            {/* Attachments Section */}
            {selectedActivity.attachments && selectedActivity.attachments.length > 0 && (
              <Card 
                title={
                  <Space>
                    <PaperClipOutlined style={{ color: '#1890ff' }} />
                    <Text strong>Ekler ({selectedActivity.attachments.length})</Text>
                  </Space>
                }
                style={{ marginTop: '16px' }}
                size="small"
              >
                <List
                  grid={{ gutter: 16, xs: 1, sm: 2, md: 2, lg: 3, xl: 3, xxl: 3 }}
                  dataSource={selectedActivity.attachments}
                  renderItem={(attachment: any) => {
                    const isImage = attachment.fileType?.startsWith('image/');
                    const fileName = attachment.url?.split('-').pop() || 'Dosya';
                    
                    return (
                      <List.Item>
                        <Card
                          size="small"
                          style={{
                            border: '1px solid #f0f0f0',
                            borderRadius: '8px',
                            overflow: 'hidden'
                          }}
                          bodyStyle={{ padding: '12px' }}
                        >
                          <div style={{ textAlign: 'center', marginBottom: '8px' }}>
                            {isImage ? (
                              <FileImageOutlined style={{ fontSize: '32px', color: '#722ed1' }} />
                            ) : (
                              <FilePdfOutlined style={{ fontSize: '32px', color: '#ff4d4f' }} />
                            )}
                          </div>
                          
                          <div style={{ 
                            fontSize: '12px', 
                            color: '#595959',
                            marginBottom: '8px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            {fileName}
                          </div>

                          <Space direction="vertical" style={{ width: '100%' }} size="small">
                            <Button
                              type="link"
                              size="small"
                              icon={<EyeOutlined />}
                              block
                              onClick={async () => {
                                try {
                                  await FileService.getInstance().viewFileAsBlob(attachment.url);
                                } catch (error) {
                                  message.error('Dosya görüntülenirken hata oluştu');
                                }
                              }}
                            >
                              Görüntüle
                            </Button>
                            <Button
                              type="link"
                              size="small"
                              icon={<DownloadOutlined />}
                              block
                              onClick={async () => {
                                try {
                                  await FileService.getInstance().downloadFileAsBlob(
                                    attachment.url,
                                    fileName
                                  );
                                } catch (error) {
                                  message.error('Dosya indirilirken hata oluştu');
                                }
                              }}
                            >
                              İndir
                            </Button>
                          </Space>

                          <div style={{ 
                            fontSize: '10px', 
                            color: '#8c8c8c',
                            marginTop: '8px',
                            textAlign: 'center'
                          }}>
                            {dayjs(attachment.createdAt).format('DD.MM.YYYY HH:mm')}
                          </div>
                        </Card>
                      </List.Item>
                    );
                  }}
                />
              </Card>
            )}
          </div>
        )}
      </Modal>

      {/* Activity Location History Modal */}
      {selectedActivityForLocation && (
        <ActivityLocationHistoryModal
          visible={locationHistoryModalVisible}
          onClose={() => {
            setLocationHistoryModalVisible(false);
            setSelectedActivityForLocation(null);
          }}
          activityId={selectedActivityForLocation.id}
          activityTitle={selectedActivityForLocation.title}
        />
      )}
    </>
  );
};

export default PersonelActivityDetailPage;

